import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useClientInventory } from '../hooks/useInventory';
import { useStorageSummary } from '../hooks/useInventory';
import {
  useCreateDispatchByGateEntry,
  useDispatch,
  useDispatchItems,
  useAddDispatchItem,
  useUpdateDispatchItem,
  useConfirmDispatch,
  useDeleteDispatchItem
} from '../hooks/useDispatch';
import { gateEntryService } from '../services/gateEntryService';
import { dispatchService } from '../services/dispatchService';
import { InventoryItem, StorageSummaryItem } from '../types';
import { Spinner, Modal } from '../components/common';
import MobileCardList, { TableColumn } from '../components/common/MobileCardList';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../hooks/useConfirm';
import { useToast } from '../contexts/ToastContext';
import { useUser } from '../hooks/useUsers';
import { INPUT_SANITIZERS, FIELD_VALIDATORS } from '../utils/validationHelpers';

// LR Date defaults to today but stays editable.
const todayStr = () => new Date().toISOString().slice(0, 10);

interface ItemFormData {
  goodQtyOut: number;
  cdamQtyOut: number;
  damQtyOut: number;
  palletsOut: number;
}

export default function DispatchCreate() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { gateEntryId } = useParams<{ gateEntryId: string }>();
  const { confirm, ConfirmDialog } = useConfirm();
  const toast = useToast();
  
  // Get gateEntryId from navigation state or URL param
  const gateEntryIdNumber = location.state?.gateEntryId || (gateEntryId ? parseInt(gateEntryId) : null);
  const existingDispatchId = location.state?.dispatchId || null; // Check if we're viewing an existing dispatch
  // Reached via Outward's "Edit" on a completed dispatch (reopened) — relabel
  // the form "Edit Past Dispatch".
  const isPastEdit = Boolean(location.state?.pastEdit);

  const [dispatchId, setDispatchId] = useState<number | null>(existingDispatchId);
  const isViewingExisting = !!existingDispatchId; // Check if we're viewing an existing dispatch
  const [gateEntry, setGateEntry] = useState<any>(null);
  const [clientId, setClientId] = useState<number | null>(null);
  const [currentItem, setCurrentItem] = useState<InventoryItem | null>(null);
  const [showStorageModal, setShowStorageModal] = useState(false);
  const [storageData, setStorageData] = useState<StorageSummaryItem[]>([]);
  const [itemForm, setItemForm] = useState<ItemFormData>({
    goodQtyOut: 0,
    cdamQtyOut: 0,
    damQtyOut: 0,
    palletsOut: 0,
  });
  const [isInitializing, setIsInitializing] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  // Existing storage selections for the item being edited, stashed here so the
  // storageSummary merge effect below can populate Dispatch Qty / Remove onto
  // the matching rows once the full location list loads.
  const [editingItemStorageSelections, setEditingItemStorageSelections] = useState<any[] | null>(null);
  const [remarks, setRemarks] = useState<string>('');
  const [destination, setDestination] = useState<string>('');
  const [lrNumber, setLrNumber] = useState<string>('');
  const [lrNumberError, setLrNumberError] = useState<string | null>(null);
  const [eWaybillNumber, setEWaybillNumber] = useState<string>('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [driverName, setDriverName] = useState('');
  const [driverPhone, setDriverPhone] = useState('');
  const [transporterName, setTransporterName] = useState('');
  const [lrDate, setLrDate] = useState(todayStr());
  const [handlingType, setHandlingType] = useState<'manual' | 'mhe' | ''>('');
  const [isSharedPallet, setIsSharedPallet] = useState(false);

  const getErrorMessage = (error: any, fallback: string) => {
    if (error?.errors && Array.isArray(error.errors)) {
      return error.errors[0];
    }
    return error?.message || fallback;
  };

  // Hooks
  const createDispatchMutation = useCreateDispatchByGateEntry();
  const { data: dispatchData } = useDispatch(dispatchId);
  const {
    data: dispatchItemsData,
    isLoading: itemsLoading,
    isError: itemsError,
    error: itemsErrorData,
  } = useDispatchItems(dispatchId);
  const addItemMutation = useAddDispatchItem();
  const updateItemMutation = useUpdateDispatchItem();
  const confirmDispatchMutation = useConfirmDispatch();
  const deleteItemMutation = useDeleteDispatchItem();

  // Get client data - only fetch when clientId is available
  const { data: inventoryData, isLoading: inventoryLoading } = useClientInventory(
    clientId || 0,
    { limit: 100 },
  );

  // Get approver details if dispatch is approved
  const approvedBy = dispatchData?.approvedBy;
  const { data: approverData } = useUser(approvedBy);

  const { data: storageSummary, isLoading: storageLoading } = useStorageSummary(
    currentItem ? {
      clientId: clientId!,
      productDescription: currentItem.productDescription,
      batchNo: currentItem.batchNo,
    } : null
  );

  // Dispatch items
  const items = useMemo(() => {
    return dispatchItemsData || [];
  }, [dispatchItemsData]);

  // Initialize Dispatch on page load
  useEffect(() => {
    // Guard: Only run once
    if (hasInitialized) return;
    if (!gateEntryIdNumber) return;
    
    const initializeDispatch = async () => {
      try {
        // Fetch gate entry details
        const gateEntryResponse = await gateEntryService.getGateEntryById(gateEntryIdNumber.toString());
        const gateEntryData = gateEntryResponse.data;
        setGateEntry(gateEntryData);
        setClientId(gateEntryData.clientId);

        console.log('Gate entry loaded:', {
          id: gateEntryData.id,
          status: gateEntryData.status,
          dispatchId: gateEntryData.dispatchId,
        });

        // If we're viewing an existing dispatch (passed via navigation state), use it
        if (isViewingExisting && existingDispatchId) {
          console.log('Viewing existing dispatch from navigation state:', existingDispatchId);
          setDispatchId(existingDispatchId);
          setIsInitializing(false);
          setHasInitialized(true);
          return;
        }

        // Check if gate entry already has a dispatch
        if (gateEntryData.dispatchId) {
          console.log('Gate entry already has dispatch:', gateEntryData.dispatchId);
          setDispatchId(gateEntryData.dispatchId);
          setIsInitializing(false);
          setHasInitialized(true);
          return;
        }

        // If status indicates dispatch exists but no dispatchId, try to fetch dispatches
        if (gateEntryData.status === 'pick_slip_created' || gateEntryData.status === 'dispatched') {
          console.log('Gate entry has dispatch status but no dispatchId, fetching dispatches...');
          try {
            // Fetch dispatches to find the one for this gate entry
            const dispatchesData = await dispatchService.getDispatches({ limit: 100 });
            const dispatch = dispatchesData?.items?.find((d: any) => d.gateEntryId === gateEntryIdNumber);
            
            if (dispatch) {
              console.log('Found existing dispatch:', dispatch.id);
              setDispatchId(dispatch.id);
              setIsInitializing(false);
              setHasInitialized(true);
              return;
            } else {
              console.warn('No dispatch found for gate entry, but status indicates one should exist');
            }
          } catch (error) {
            console.error('Failed to fetch dispatches:', error);
            // Continue to show error below
          }
        }

        // Only create dispatch if status is dock_assigned
        if (gateEntryData.status !== 'dock_assigned') {
          console.error('Cannot create dispatch - gate entry status is:', gateEntryData.status);
          toast.error(`Cannot create dispatch. Gate entry must be dock assigned first. Current status: ${gateEntryData.status}`);
          navigate('/outward');
          return;
        }

        console.log('Creating dispatch for gate entry:', gateEntryIdNumber);
        try {
          const dispatchResponse = await createDispatchMutation.mutateAsync(gateEntryIdNumber);
          setDispatchId(dispatchResponse.id);
          console.log('Dispatch created successfully:', dispatchResponse);
        } catch (createError: any) {
          console.error('Dispatch creation error:', createError);
          throw createError;
        }
      } catch (error: any) {
        console.error('Failed to initialize dispatch:', error);
        const message = error?.statusCode === 403
          ? "Access Denied: You don't have permission to create this dispatch. Please contact your administrator if you need access."
          : (error?.message || getErrorMessage(error, 'Failed to initialize dispatch'));
        toast.error(message);
        navigate('/outward');
      } finally {
        setIsInitializing(false);
        setHasInitialized(true);
      }
    };

    initializeDispatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateEntryIdNumber]);

  useEffect(() => {
    if (storageSummary) {
      // Identify a location the same way regardless of which naming a saved
      // selection used (zoneCode/rackCode/locationCode vs legacy zone/rack/location) —
      // zoneLocationCode (or bare zoneCode when there's no specific slot) plus
      // rackCode+locationCode uniquely pins down one physical storage row.
      const keyOf = (s: any) =>
        `${s.zoneLocationCode ?? s.zoneCode ?? s.zone ?? ''}|${s.rackCode ?? s.rack ?? ''}|${s.locationCode ?? s.location ?? ''}`;
      const selectionsByKey = new Map(
        (editingItemStorageSelections ?? []).map((s: any) => [keyOf(s), s]),
      );
      const rows = storageSummary.map((item: any) => {
        const match = selectionsByKey.get(keyOf(item));
        return {
          ...item,
          palletsToRemove: match?.palletsToRemove ?? match?.palletsToDispatch ?? 0,
          dispatchQty: match?.quantityToDispatch ?? 0,
        };
      });
      setStorageData(rows);
      // Auto-detect: if every storage row has palletCount=0, this is a shared pallet item
      if (!editingItemId && rows.length > 0) {
        const allShared = rows.every((r: any) => (r.palletCount || 0) === 0);
        setIsSharedPallet(allShared);
      }
    }
  }, [storageSummary, editingItemId, editingItemStorageSelections]);

  // Good Qty Out is derived from the per-location Dispatch Qty entries below —
  // it's the sum across non-LBD (good stock) locations, not a manual total.
  useEffect(() => {
    if (isSharedPallet) return;
    const totalGoodDispatchQty = storageData
      .filter((s) => !s.isLbd)
      .reduce((sum, s) => sum + (s.dispatchQty || 0), 0);
    setItemForm((prev) =>
      prev.goodQtyOut === totalGoodDispatchQty ? prev : { ...prev, goodQtyOut: totalGoodDispatchQty },
    );
  }, [storageData, isSharedPallet]);

  // DAM Qty Out is derived the same way, summed across LBD locations instead —
  // the backend has no way to split a location's stock into CDAM vs DAM, so
  // every LBD unit dispatched is attributed to DAM. CDAM Qty Out is fixed at 0
  // and locked (see the read-only field below) rather than left to guess a split.
  useEffect(() => {
    if (isSharedPallet) return;
    const totalLbdDispatchQty = storageData
      .filter((s) => s.isLbd)
      .reduce((sum, s) => sum + (s.dispatchQty || 0), 0);
    setItemForm((prev) =>
      prev.damQtyOut === totalLbdDispatchQty && prev.cdamQtyOut === 0
        ? prev
        : { ...prev, damQtyOut: totalLbdDispatchQty, cdamQtyOut: 0 },
    );
  }, [storageData, isSharedPallet]);

  // Pallets Out is derived from the per-location Remove entries below — Remove
  // itself is no longer manually typed, only auto-populated when a location's
  // Dispatch Qty fully depletes it, so the total just mirrors that sum.
  useEffect(() => {
    if (isSharedPallet) return;
    const totalPalletsToRemove = storageData.reduce((sum, s) => sum + (s.palletsToRemove || 0), 0);
    setItemForm((prev) =>
      prev.palletsOut === totalPalletsToRemove ? prev : { ...prev, palletsOut: totalPalletsToRemove },
    );
  }, [storageData, isSharedPallet]);

  // Initialize form fields from existing dispatch data
  useEffect(() => {
    if (dispatchData?.remarks && !remarks)               setRemarks(dispatchData.remarks);
    if (dispatchData?.destination && !destination)       setDestination(dispatchData.destination);
    if (dispatchData?.lrNumber && !lrNumber)             setLrNumber(dispatchData.lrNumber);
    if (dispatchData?.eWaybillNumber && !eWaybillNumber) setEWaybillNumber(dispatchData.eWaybillNumber);
    if (dispatchData?.vehicleNo && !vehicleNo)           setVehicleNo(dispatchData.vehicleNo);
    if (dispatchData?.driverName && !driverName)         setDriverName(dispatchData.driverName);
    if (dispatchData?.driverPhone && !driverPhone)       setDriverPhone(dispatchData.driverPhone);
    if (dispatchData?.transporterName && !transporterName) setTransporterName(dispatchData.transporterName);
    if (dispatchData?.lrDate && !lrDate)                 setLrDate(dispatchData.lrDate);
    if (dispatchData?.handlingType && !handlingType)     setHandlingType(dispatchData.handlingType);
    // Only re-run when the fetched dispatch changes; setters/state are intentionally omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchData]);

  const handleSelectInventoryItem = (item: InventoryItem) => {
    // Map id to inventoryId if inventoryId is missing
    const itemWithInventoryId = {
      ...item,
      inventoryId: item.inventoryId || (item as any).id
    };
    
    setCurrentItem(itemWithInventoryId);
    setEditingItemId(null);
    setEditingItemStorageSelections(null);
    setIsSharedPallet(false);
    setItemForm({
      goodQtyOut: 0,
      cdamQtyOut: 0,
      damQtyOut: 0,
      palletsOut: 0,
    });
    setShowStorageModal(true);
  };

  // Edit item - populate modal with item data
  const handleEditItem = (item: any) => {
    setEditingItemId(item.id);
    
    // Find the actual inventory item to get current available quantities
    const inventoryItem = inventoryData?.data?.find(
      (inv: InventoryItem) => inv.inventoryId === item.inventoryId
    );
    
    if (inventoryItem) {
      // Use the inventory item with correct inventoryId
      setCurrentItem(inventoryItem);
    } else {
      // Fallback: inventory row not in the loaded list. Use the available
      // quantities the backend sends on the dispatch item (the current inventory
      // balance) — NOT item.goodQty, which is the already-dispatched quantity.
      setCurrentItem({
        inventoryId: item.inventoryId,
        productDescription: item.productDescription,
        batchNo: item.batchNo,
        goodQty: item.availableGoodQty ?? item.goodQty,
        cdamQty: item.availableCdamQty ?? item.cdamQty,
        damQty: item.availableDamQty ?? item.damQty,
      } as InventoryItem);
    }
    
    setIsSharedPallet(!item.palletCount && (!item.storageSelections || item.storageSelections.length === 0));
    setItemForm({
      goodQtyOut: item.goodQty,
      // CDAM Qty Out is retired — LBD dispatch quantity is now tracked entirely
      // under DAM Qty Out (derived below), so this stays locked at 0 rather than
      // resurrecting whatever a legacy item had recorded.
      cdamQtyOut: 0,
      damQtyOut: item.damQty,
      palletsOut: item.palletCount,
    });

    // Stash the item's existing storage selections so the storageSummary merge
    // effect above can populate Dispatch Qty / Remove onto the matching rows
    // once the full location list loads — setting storageData directly here
    // gets clobbered as soon as storageSummary arrives (which lists every
    // location, not just the ones previously selected) and would reset
    // everything back to 0.
    setEditingItemStorageSelections(item.storageSelections ?? null);

    setShowStorageModal(true);
  };

  const handleStorageSelection = async () => {
    if (!currentItem || !dispatchId) return;

    // Pallets removed is now fully derived from per-location Remove entries
    // (auto-set only on full depletion), so a valid dispatch can legitimately
    // remove zero pallets — e.g. an all-partial dispatch where every location
    // keeps its pallet and only its quantity drops. No pallet-count gate here.

    if (itemForm.goodQtyOut > currentItem.goodQty) {
      toast.error('Good quantity out cannot exceed available good quantity');
      return;
    }

    // CDAM Qty Out is retired (always 0) — DAM Qty Out now carries the combined
    // LBD dispatch total, so it's validated against CDAM + DAM available together,
    // not DAM alone, otherwise a dispatch pulling from both would be wrongly blocked.
    if (itemForm.damQtyOut > currentItem.cdamQty + currentItem.damQty) {
      toast.error('DAM quantity out cannot exceed available CDAM + DAM quantity');
      return;
    }

    // In shared-pallet mode the good stock is dispatched qty-only (no good pallet
    // removed), but the user may still remove dedicated LBD (damaged-goods) pallets.
    // Outside shared mode any selected pallet (good or LBD) is removed. A row also
    // counts as active on dispatchQty alone (partial removal, pallet stays put) so
    // its per-location quantity still gets sent and the storage row stays in sync.
    const activeSelections = storageData.filter((s) => {
      const hasActivity = (s.palletsToRemove || 0) > 0 || (s.dispatchQty || 0) > 0;
      if (!hasActivity) return false;
      return isSharedPallet ? !!s.isLbd : true;
    });
    const activeSelectionPallets = activeSelections.reduce(
      (sum, s) => sum + (s.palletsToRemove || 0),
      0,
    );

    try {
      const payload = {
        inventoryId: currentItem.inventoryId,
        productDescription: currentItem.productDescription,
        batchNo: currentItem.batchNo,
        goodQtyOut: itemForm.goodQtyOut,
        cdamQtyOut: itemForm.cdamQtyOut,
        damQtyOut: itemForm.damQtyOut,
        palletsOut: isSharedPallet ? activeSelectionPallets : itemForm.palletsOut,
        storageSelections: activeSelections.map(s => ({
          zoneCode: s.zoneCode || '',
          zoneLocationCode: s.zoneLocationCode || '',
          rackCode: s.rackCode || '',
          locationCode: s.locationCode || '',
          palletsToRemove: s.palletsToRemove || 0,
          quantityToDispatch: s.dispatchQty || 0,
        })),
      };

      if (editingItemId) {
        await updateItemMutation.mutateAsync({
          dispatchId,
          itemId: editingItemId,
          payload,
        });
      } else {
        await addItemMutation.mutateAsync({ dispatchId, payload });
      }

      // Clear form and close modal
      setItemForm({
        goodQtyOut: 0,
        cdamQtyOut: 0,
        damQtyOut: 0,
        palletsOut: 0,
      });
      setShowStorageModal(false);
      setCurrentItem(null);
      setEditingItemId(null);
      setEditingItemStorageSelections(null);
      toast.success(editingItemId ? 'Item updated successfully' : 'Item added successfully');
    } catch (error: any) {
      toast.error(getErrorMessage(error, editingItemId ? 'Failed to update item' : 'Failed to add item'));
    }
  };

  // Delete item
  const handleDeleteItem = async (itemId: number) => {
    const confirmed = await confirm({
      title: 'Delete Item',
      message: 'Are you sure you want to delete this item?',
      confirmText: 'Delete',
      variant: 'danger',
    });

    if (!confirmed) return;

    if (!dispatchId) {
      toast.error('Dispatch not initialized');
      return;
    }

    try {
      await deleteItemMutation.mutateAsync({ dispatchId, itemId });
      toast.success('Item deleted successfully');
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Failed to delete item'));
    }
  };

  // Confirm Dispatch
  const handleConfirmDispatch = async () => {
    if (items.length === 0) {
      toast.error('Please add at least one item before confirming');
      return;
    }

    if (eWaybillNumber && eWaybillNumber.length !== 12) {
      toast.error('E-Waybill Number must be exactly 12 digits');
      return;
    }

    const lrNumberValidationError = FIELD_VALIDATORS.lrNumber(lrNumber);
    if (lrNumberValidationError) {
      setLrNumberError(lrNumberValidationError);
      toast.error(lrNumberValidationError);
      return;
    }

    const vehicleNoError = FIELD_VALIDATORS.vehicleNumber(vehicleNo, false);
    if (vehicleNoError) {
      toast.error(vehicleNoError);
      return;
    }

    const driverPhoneError = FIELD_VALIDATORS.mobileNumber(driverPhone, false);
    if (driverPhoneError) {
      toast.error(driverPhoneError);
      return;
    }

    if (lrDate && lrDate > todayStr()) {
      toast.error('LR Date cannot be in the future');
      return;
    }

    if (!handlingType) {
      toast.error('Handling Type is required');
      return;
    }

    const confirmed = await confirm({
      title: 'Confirm Dispatch',
      message: 'Are you sure you want to confirm this dispatch? This action cannot be undone.',
      confirmText: 'Confirm Dispatch',
      variant: 'primary',
    });

    if (!confirmed) return;

    try {
      await confirmDispatchMutation.mutateAsync({
        dispatchId: dispatchId!,
        remarks,
        destination,
        lrNumber:        lrNumber        || undefined,
        eWaybillNumber:  eWaybillNumber  || undefined,
        vehicleNo:       vehicleNo       || undefined,
        driverName:      driverName      || undefined,
        driverPhone:     driverPhone     || undefined,
        transporterName: transporterName || undefined,
        lrDate:          lrDate          || undefined,
        handlingType:    handlingType    || undefined,
      });
      toast.success('Dispatch confirmed successfully');
      navigate('/outward');
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Failed to confirm dispatch'));
    }
  };

  // Check dispatch status - handle both nested and direct response structures
  const currentStatus = dispatchData?.status;
  const isDraft = currentStatus === 'draft';
  const isSubmitted = currentStatus === 'submitted';
  const isApproved = currentStatus === 'approved';
  const isRejected = currentStatus === 'rejected';

  // Shared by the desktop select-items table and the mobile card list
  const inventorySelectColumns: TableColumn[] = [
    { key: 'productDescription', label: 'Product' },
    {
      key: 'skuCode',
      label: 'SKU Code',
      render: (value: any) => (
        <span className="font-semibold text-gray-900">{value || '-'}</span>
      ),
    },
    { key: 'customerSapCode', label: 'SAP Code' },
    { key: 'batchNo', label: 'Batch No' },
    {
      key: 'mfgDate',
      label: 'MFG Date',
      render: (value: any) => (value ? new Date(value).toLocaleDateString() : '-'),
    },
    {
      key: 'expiryDate',
      label: 'Expiry Date',
      render: (value: any) => (value ? new Date(value).toLocaleDateString() : '-'),
    },
    { key: 'goodQty', label: 'Good Qty' },
    { key: 'cdamQty', label: 'CDAM Qty' },
    { key: 'damQty', label: 'DAM Qty' },
    {
      key: 'actions',
      label: 'Action',
      render: (_: any, row: any) => {
        if (!hasPermission('dispatch:create')) return null;
        const alreadyAdded = items.some(
          (it: any) => it.inventoryId === (row.inventoryId ?? row.id),
        );
        return (
          <button
            onClick={() => handleSelectInventoryItem(row)}
            disabled={alreadyAdded}
            title={alreadyAdded ? 'Already added to this dispatch' : undefined}
            className={`w-full py-2 min-h-[42px] text-white text-sm rounded ${
              alreadyAdded
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-brand-600 hover:bg-brand-700 active:scale-[0.97]'
            }`}
          >
            {alreadyAdded ? 'Added' : 'Select'}
          </button>
        );
      },
    },
  ];

  // Shared by the desktop dispatch-items table and the mobile card list
  const dispatchItemColumns: TableColumn[] = [
    { key: 'productDescription', label: 'Product' },
    {
      key: 'skuCode',
      label: 'SKU Code',
      render: (value: any) => (
        <span className="font-semibold text-gray-900">{value || '-'}</span>
      ),
    },
    { key: 'customerSapCode', label: 'SAP Code' },
    { key: 'batchNo', label: 'Batch No' },
    {
      key: 'mfgDate',
      label: 'MFG Date',
      render: (value: any) => (value ? new Date(value).toLocaleDateString() : '-'),
    },
    {
      key: 'expiryDate',
      label: 'Expiry Date',
      render: (value: any) => (value ? new Date(value).toLocaleDateString() : '-'),
    },
    { key: 'goodQty', label: 'Good Out' },
    { key: 'cdamQty', label: 'CDAM Out' },
    { key: 'damQty', label: 'DAM Out' },
    {
      key: 'palletCount',
      label: 'Pallets Out',
      render: (value: any) =>
        value === 0 ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
            <i className="fas fa-layer-group text-xs"></i> Shared
          </span>
        ) : (
          value
        ),
    },
    {
      key: 'storageSelections',
      label: 'Storage Locations',
      render: (_: any, row: any) =>
        row.palletCount === 0 ? (
          <span className="text-xs text-amber-600 font-medium">Shared pallet — qty only</span>
        ) : (
          <div className="space-y-1">
            {row.storageSelections?.map((storage: any, index: number) => (
              <div key={index} className="text-xs bg-gray-50 p-2 rounded text-left">
                <div className="font-medium">
                  {storage.palletsToDispatch} pallets removed from:
                </div>
                <div className="text-gray-600 mt-1">
                  {[
                    (storage.zoneLocation || storage.zone) && `Zone: ${storage.zoneLocation || storage.zone}`,
                    storage.rack && `Rack: ${storage.rack}`,
                    storage.location && `Location: ${storage.location}`,
                  ].filter(Boolean).join(' | ') || `Storage ID: ${storage.inventoryStorageId}`}
                </div>
              </div>
            )) || '-'}
          </div>
        ),
    },
    ...(isDraft || isRejected
      ? [
          {
            key: 'actions',
            label: 'Actions',
            render: (_: any, row: any) => (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleEditItem(row)}
                  className="flex-1 py-2 min-h-[42px] rounded border border-brand-200 bg-brand-50 text-brand-600 text-xs font-medium active:scale-[0.97]"
                  title="Edit item"
                >
                  ✏️ Edit
                </button>
                <button
                  onClick={() => handleDeleteItem(row.id)}
                  className="flex-1 py-2 min-h-[42px] rounded border border-red-200 bg-red-50 text-red-600 text-xs font-medium active:scale-[0.97]"
                  title="Delete item"
                >
                  🗑️ Delete
                </button>
              </div>
            ),
          } as TableColumn,
        ]
      : []),
  ];

  // Loading state
  if (isInitializing) {
    return (
      <Layout>
        <div className="min-h-screen px-1 py-6 lg:p-6 bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Initializing Dispatch...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!gateEntry || inventoryLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Spinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen px-1 py-6 lg:p-6 bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Header */}
        <div className="mb-6 transform transition-all duration-300 hover:scale-[1.01]">
          <div className="flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-brand-100 to-brand-100 p-6 rounded-2xl shadow-xl border-2 border-brand-300 hover:shadow-2xl transition-shadow duration-300">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-brand-600 to-brand-700 rounded-xl shadow-lg transform transition-transform duration-300 hover:scale-110 hover:rotate-3">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">
                  {isPastEdit ? 'Edit Past Dispatch' : 'Create Dispatch'}
                </h2>
                <p className="text-gray-700 mt-1 font-medium">
                  {isPastEdit ? 'Editing a completed dispatch — resubmit for approval' : 'Outbound Processing'} - Gate Entry: {gateEntry?.entryNumber}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/outward')}
              className="w-full lg:w-auto justify-center px-6 py-3 min-h-[44px] lg:min-h-0 bg-white text-gray-700 rounded-xl hover:bg-gray-50 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center space-x-2 border-2 border-gray-300"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="whitespace-nowrap">Back to Outbound</span>
            </button>
          </div>
        </div>

        {/* Status Bar */}
        {dispatchData && (
          <div className={`mb-4 p-4 rounded-xl flex flex-wrap justify-between items-center gap-2 shadow-lg transform transition-all duration-300 hover:scale-[1.01] ${
            isSubmitted ? 'bg-yellow-50 border border-yellow-200' :
            isApproved ? 'bg-green-50 border border-green-200' :
            isRejected ? 'bg-red-50 border border-red-200' :
            'bg-brand-50 border border-brand-200'
          }`}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-sm font-medium text-gray-700">Status:</span>
              <span className={`font-semibold ${
                isSubmitted ? 'text-yellow-700' :
                isApproved ? 'text-green-700' :
                isRejected ? 'text-red-700' :
                'text-brand-700'
              }`}>
                {isDraft && 'Draft'}
                {isSubmitted && 'Submitted for approval'}
                {isApproved && 'Approved'}
                {isRejected && (
                  <>
                    Rejected{dispatchData?.rejectionReason && ` - ${dispatchData.rejectionReason}`}
                  </>
                )}
              </span>
              {isApproved && approverData && (
                <>
                  <span className="text-gray-400">•</span>
                  <span className="text-sm text-gray-600">
                    Approved by: <span className="font-semibold text-gray-900">{approverData.name || approverData.email}</span>
                  </span>
                  {dispatchData?.approvedAt && (
                    <>
                      <span className="text-gray-400">•</span>
                      <span className="text-sm text-gray-600">
                        {new Date(dispatchData.approvedAt).toLocaleString()}
                      </span>
                    </>
                  )}
                </>
              )}
            </div>
            {dispatchData?.dispatchNumber && (
              <span className="text-sm text-gray-600">
                Dispatch: <span className="font-semibold">{dispatchData.dispatchNumber}</span>
              </span>
            )}
          </div>
        )}

        {/* Dispatch Header - Read Only */}
        <div className="card mb-6 transform transition-all duration-300 hover:shadow-xl animate-fade-in">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-gradient-to-br from-brand-500 to-brand-600 rounded-lg mr-3 shadow-md">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Gate Entry Details</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 bg-gradient-to-r from-brand-50 to-brand-50 p-5 rounded-xl border-2 border-brand-200 shadow-sm">
            <div className="min-w-0 transform transition-all duration-200 hover:scale-105">
              <p className="text-xs text-brand-600 font-medium mb-1">Entry Number</p>
              <p className="text-sm font-semibold text-gray-900 truncate">{gateEntry?.entryNumber || dispatchData?.gateEntryNumber || '-'}</p>
            </div>
            <div className="min-w-0 transform transition-all duration-200 hover:scale-105">
              <p className="text-xs text-brand-600 font-medium mb-1">Client Name</p>
              <p className="text-sm font-semibold text-gray-900 truncate">{dispatchData?.clientName || gateEntry?.client?.companyName || '-'}</p>
            </div>
            <div className="min-w-0 transform transition-all duration-200 hover:scale-105">
              <p className="text-xs text-brand-600 font-medium mb-1">Vehicle Number</p>
              <p className="text-sm font-semibold text-gray-900 truncate">{gateEntry?.vehicleNumber || '-'}</p>
            </div>
            <div className="min-w-0 transform transition-all duration-200 hover:scale-105">
              <p className="text-xs text-brand-600 font-medium mb-1">Driver Name</p>
              <p className="text-sm font-semibold text-gray-900 truncate">{gateEntry?.driverName || '-'}</p>
            </div>
            <div className="min-w-0 transform transition-all duration-200 hover:scale-105">
              <p className="text-xs text-brand-600 font-medium mb-1">Expected Qty</p>
              <p className="text-sm font-semibold text-gray-900 truncate">{gateEntry?.expectedQuantity || '-'}</p>
            </div>
          </div>
          {dispatchData?.dispatchNumber && (
            <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Dispatch Number:</span> {dispatchData.dispatchNumber}
              </p>
            </div>
          )}
        </div>

        {/* Dispatch Information Section */}
        {(isDraft || isRejected) && (
          <div className="card mb-6 overflow-hidden">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
              <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
                <i className="fas fa-truck text-brand-600 text-base"></i>
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Dispatch Information</h3>
                <p className="text-xs text-gray-500 mt-0.5">Optional details attached to this dispatch</p>
              </div>
            </div>
            <div className="space-y-5">
              {/* Transport fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                <div className="space-y-1.5">
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                    <i className="fas fa-car text-orange-400 text-xs"></i>
                    Vehicle Number
                  </label>
                  <input
                    type="text"
                    value={vehicleNo}
                    onChange={e => setVehicleNo(INPUT_SANITIZERS.vehicleFormat(e.target.value))}
                    placeholder="e.g. MH04AB1234"
                    maxLength={10}
                    className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors ${
                      vehicleNo && FIELD_VALIDATORS.vehicleNumber(vehicleNo, false) ? 'border-red-400 bg-red-50' : 'border-gray-200'
                    }`}
                  />
                  {vehicleNo && FIELD_VALIDATORS.vehicleNumber(vehicleNo, false) && (
                    <p className="text-xs text-red-500">{FIELD_VALIDATORS.vehicleNumber(vehicleNo, false)}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                    <i className="fas fa-user text-orange-400 text-xs"></i>
                    Driver Name
                  </label>
                  <input
                    type="text"
                    value={driverName}
                    onChange={e => setDriverName(e.target.value.replace(/[^a-zA-Z\s]/g, '').slice(0, 100))}
                    placeholder="e.g. Raju Patil"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                    <i className="fas fa-phone text-orange-400 text-xs"></i>
                    Driver Phone
                  </label>
                  <input
                    type="tel"
                    value={driverPhone}
                    onChange={e => setDriverPhone(INPUT_SANITIZERS.digitsOnly(e.target.value).slice(0, 10))}
                    placeholder="e.g. 9876543210"
                    className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors ${
                      driverPhone && FIELD_VALIDATORS.mobileNumber(driverPhone, false) ? 'border-red-400 bg-red-50' : 'border-gray-200'
                    }`}
                  />
                  {driverPhone && FIELD_VALIDATORS.mobileNumber(driverPhone, false) && (
                    <p className="text-xs text-red-500">{FIELD_VALIDATORS.mobileNumber(driverPhone, false)}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                    <i className="fas fa-building text-orange-400 text-xs"></i>
                    Transporter Name
                  </label>
                  <input
                    type="text"
                    value={transporterName}
                    onChange={e => setTransporterName(INPUT_SANITIZERS.companyNameFormat(e.target.value))}
                    placeholder="e.g. Fast Cargo Pvt Ltd"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                    <i className="fas fa-calendar text-orange-400 text-xs"></i>
                    LR Date
                  </label>
                  <input
                    type="date"
                    value={lrDate}
                    max={todayStr()}
                    onChange={e => {
                      if (e.target.value > todayStr()) {
                        toast.error('LR Date cannot be in the future');
                        return;
                      }
                      setLrDate(e.target.value);
                    }}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                    <i className="fas fa-dolly text-orange-400 text-xs"></i>
                    Handling Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={handlingType}
                    onChange={e => setHandlingType(e.target.value as 'manual' | 'mhe' | '')}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors"
                  >
                    <option value="">— Select —</option>
                    <option value="manual">Manual</option>
                    <option value="mhe">MHE</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700" htmlFor="dispatch-lr-number">
                    <i className="fas fa-receipt text-brand-400 text-xs"></i>
                    LR Number <span className="text-gray-400 font-normal text-xs">(Lorry Receipt)</span>
                  </label>
                  <input
                    id="dispatch-lr-number"
                    type="text"
                    value={lrNumber}
                    onChange={(e) => {
                      setLrNumber(INPUT_SANITIZERS.lrNumberFormat(e.target.value));
                      if (lrNumberError) setLrNumberError(null);
                    }}
                    onBlur={() => setLrNumberError(FIELD_VALIDATORS.lrNumber(lrNumber))}
                    placeholder="e.g. LR2026001234 or LR-2026-001234"
                    className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 text-sm transition-colors ${lrNumberError ? 'border-red-400 focus:ring-red-400 focus:border-red-400' : 'border-gray-200 focus:ring-brand-500 focus:border-brand-500'}`}
                  />
                  {lrNumberError && (
                    <p className="text-xs text-red-600 mt-1">{lrNumberError}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700" htmlFor="dispatch-ewaybill">
                    <i className="fas fa-file-invoice text-green-400 text-xs"></i>
                    E-Waybill Number
                  </label>
                  <input
                    id="dispatch-ewaybill"
                    type="text"
                    inputMode="numeric"
                    value={eWaybillNumber}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '');
                      setEWaybillNumber(digits.slice(0, 12));
                    }}
                    placeholder="12-digit E-Waybill Number"
                    className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors ${
                      eWaybillNumber && eWaybillNumber.length !== 12
                        ? 'border-red-400 bg-red-50'
                        : 'border-gray-200'
                    }`}
                    maxLength={12}
                  />
                  <p className={`text-xs ${eWaybillNumber && eWaybillNumber.length !== 12 ? 'text-red-500' : 'text-gray-400'}`}>
                    {eWaybillNumber
                      ? eWaybillNumber.length === 12
                        ? '✓ Valid (12 digits)'
                        : `${eWaybillNumber.length}/12 digits`
                      : 'Must be exactly 12 digits'}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700" htmlFor="dispatch-destination">
                    <i className="fas fa-map-marker-alt text-red-400 text-xs"></i>
                    Destination
                  </label>
                  <textarea
                    id="dispatch-destination"
                    rows={4}
                    value={destination}
                    onChange={(e) => {
                      const value = e.target.value;
                      const sanitized = value.replace(/[^a-zA-Z0-9\s,.\-\n]/g, '');
                      if (sanitized.length <= 200) {
                        setDestination(sanitized);
                      }
                    }}
                    placeholder="Street Address&#10;City, State, ZIP Code"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none text-sm text-gray-800 placeholder-gray-400 transition-colors"
                    maxLength={200}
                  />
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-gray-400">Complete delivery address</p>
                    <span className={`text-xs font-medium tabular-nums ${destination.length >= 180 ? 'text-red-500' : 'text-gray-400'}`}>
                      {destination.length}/200
                    </span>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700" htmlFor="dispatch-remarks">
                    <i className="fas fa-sticky-note text-amber-400 text-xs"></i>
                    Additional Information
                  </label>
                  <textarea
                    id="dispatch-remarks"
                    rows={4}
                    value={remarks}
                    onChange={(e) => {
                      const value = e.target.value;
                      const sanitized = value.replace(/[^a-zA-Z0-9\s,.\-\n]/g, '');
                      if (sanitized.length <= 200) {
                        setRemarks(sanitized);
                      }
                    }}
                    placeholder="Remarks, special instructions, or notes for this dispatch..."
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none text-sm text-gray-800 placeholder-gray-400 transition-colors"
                    maxLength={200}
                  />
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-gray-400">Included with dispatch records</p>
                    <span className={`text-xs font-medium tabular-nums ${remarks.length >= 180 ? 'text-red-500' : 'text-gray-400'}`}>
                      {remarks.length}/200
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Display Dispatch Information if Submitted/Approved */}
        {(isSubmitted || isApproved) && (dispatchData?.destination || dispatchData?.remarks || dispatchData?.lrNumber || dispatchData?.eWaybillNumber || dispatchData?.vehicleNo || dispatchData?.driverName || dispatchData?.transporterName || dispatchData?.handlingType) && (
          <div className="card mb-6 overflow-hidden">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
              <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
                <i className="fas fa-truck text-brand-600 text-base"></i>
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Dispatch Information</h3>
                <p className="text-xs text-gray-500 mt-0.5">Details attached to this dispatch</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              {[
                { label: 'Vehicle No',    value: dispatchData?.vehicleNo,       icon: 'fa-car',        mono: true  },
                { label: 'Driver Name',   value: dispatchData?.driverName,      icon: 'fa-user',       mono: false },
                { label: 'Driver Phone',  value: dispatchData?.driverPhone,     icon: 'fa-phone',      mono: false },
                { label: 'Transporter',   value: dispatchData?.transporterName, icon: 'fa-building',   mono: false },
                { label: 'LR Date',       value: dispatchData?.lrDate ? new Date(dispatchData.lrDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null, icon: 'fa-calendar', mono: false },
                { label: 'Handling Type', value: dispatchData?.handlingType ? dispatchData.handlingType.toUpperCase() : null, icon: 'fa-dolly', mono: false },
              ].map(f => (
                <div key={f.label} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{f.label}</p>
                  <p className={`text-sm text-gray-800 ${f.mono ? 'font-mono font-semibold' : ''}`}>{f.value || '—'}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {dispatchData?.lrNumber && (
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-md bg-brand-100 flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-receipt text-brand-500 text-xs"></i>
                    </div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">LR Number</p>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 pl-8">{dispatchData.lrNumber}</p>
                </div>
              )}
              {dispatchData?.eWaybillNumber && (
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-md bg-green-100 flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-file-invoice text-green-500 text-xs"></i>
                    </div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">E-Waybill Number</p>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 pl-8">{dispatchData.eWaybillNumber}</p>
                </div>
              )}
              {dispatchData?.destination && (
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-md bg-red-100 flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-map-marker-alt text-red-500 text-xs"></i>
                    </div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Destination</p>
                  </div>
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap break-words pl-8">{dispatchData.destination}</p>
                </div>
              )}
              {dispatchData?.remarks && (
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-md bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-sticky-note text-amber-500 text-xs"></i>
                    </div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Additional Information</p>
                  </div>
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap break-words pl-8">{dispatchData.remarks}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Client Inventory */}
        {(isDraft || isRejected) && (
          <div className="card mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Client Inventory - Select Items to Dispatch</h3>
            <MobileCardList
              columns={inventorySelectColumns}
              data={inventoryData?.data || []}
              emptyMessage="No inventory available"
            />
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SAP Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Batch No</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">MFG Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expiry Date</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Good Qty</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">CDAM Qty</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">DAM Qty</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {inventoryData?.data.map((item, index) => {
                    const alreadyAdded = items.some(
                      (it: any) =>
                        it.inventoryId ===
                        ((item as any).inventoryId ?? (item as any).id),
                    );
                    return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{item.productDescription}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">{item.skuCode || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{item.customerSapCode || '-'}</td>
                      <td className="px-4 py-3 text-sm">{item.batchNo}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {item.mfgDate ? new Date(item.mfgDate).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {item.expiryDate ? new Date(item.expiryDate).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right">{item.goodQty}</td>
                      <td className="px-4 py-3 text-sm text-right">{item.cdamQty}</td>
                      <td className="px-4 py-3 text-sm text-right">{item.damQty}</td>
                      <td className="px-4 py-3 text-center">
                        {hasPermission('dispatch:create') && (
                          <button
                            onClick={() => handleSelectInventoryItem(item)}
                            disabled={alreadyAdded}
                            title={alreadyAdded ? 'Already added to this dispatch' : undefined}
                            className={`px-3 py-1 text-white text-sm rounded ${
                              alreadyAdded
                                ? 'bg-gray-300 cursor-not-allowed'
                                : 'bg-brand-600 hover:bg-brand-700'
                            }`}
                          >
                            {alreadyAdded ? 'Added' : 'Select'}
                          </button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Items List */}
        <div className="card mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Dispatch Items ({items.length})
          </h3>

          {itemsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto"></div>
            </div>
          ) : itemsError ? (
            <div className="text-center py-8">
              <p className="text-red-600 text-sm">
                {getErrorMessage(itemsErrorData, 'Failed to load dispatch items')}
              </p>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-gray-600">No items added yet</p>
            </div>
          ) : (
            <>
            <MobileCardList columns={dispatchItemColumns} data={items} />
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SAP Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Batch No</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">MFG Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expiry Date</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Good Out</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">CDAM Out</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">DAM Out</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pallets Out</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Storage Locations</th>
                    {(isDraft || isRejected) && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item: any) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{item.productDescription}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">{item.skuCode || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{item.customerSapCode || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{item.batchNo || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {item.mfgDate ? new Date(item.mfgDate).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {item.expiryDate ? new Date(item.expiryDate).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">{item.goodQty}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">{item.cdamQty}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">{item.damQty}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">
                        {item.palletCount === 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            <i className="fas fa-layer-group text-xs"></i> Shared
                          </span>
                        ) : item.palletCount}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {item.palletCount === 0 ? (
                          <span className="text-xs text-amber-600 font-medium">Shared pallet — qty only</span>
                        ) : (
                          <div className="space-y-1">
                            {item.storageSelections?.map((storage: any, index: number) => (
                              <div key={index} className="text-xs bg-gray-50 p-2 rounded">
                                <div className="font-medium">
                                  {storage.palletsToDispatch} pallets removed from:
                                </div>
                                <div className="text-gray-600 mt-1">
                                  {[
                                    (storage.zoneLocation || storage.zone) && `Zone: ${storage.zoneLocation || storage.zone}`,
                                    storage.rack && `Rack: ${storage.rack}`,
                                    storage.location && `Location: ${storage.location}`,
                                  ].filter(Boolean).join(' | ') || `Storage ID: ${storage.inventoryStorageId}`}
                                </div>
                              </div>
                            )) || '-'}
                          </div>
                        )}
                      </td>
                      {(isDraft || isRejected) && (
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditItem(item)}
                              className="text-brand-600 hover:text-brand-800 text-xs"
                              title="Edit item"
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => handleDeleteItem(item.id)}
                              className="text-red-600 hover:text-red-800 text-xs"
                              title="Delete item"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>

        {/* Confirm Dispatch Button */}
        {(isDraft || isRejected) && items.length > 0 && (
          <div className="flex justify-end">
            <button
              onClick={handleConfirmDispatch}
              disabled={confirmDispatchMutation.isPending || !hasPermission('dispatch:submit')}
              title={
                hasPermission('dispatch:submit')
                  ? 'Submit dispatch'
                  : 'You need dispatch:submit permission to confirm'
              }
              className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-lg font-semibold"
            >
              {confirmDispatchMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {isRejected ? 'Resubmit Dispatch for Approval' : 'Submit Dispatch for Approval'}
                </>
              )}
            </button>
          </div>
        )}

        {/* Storage Selection Modal */}
        <Modal
          isOpen={showStorageModal}
          onClose={() => {
            setShowStorageModal(false);
            setEditingItemId(null);
            setEditingItemStorageSelections(null);
          }}
          title={editingItemId ? "Edit Item - Select Storage and Quantities" : "Select Storage and Quantities"}
          size="xl"
        >
          {currentItem && (
            <div className="space-y-4">
              {/* Product Info */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="font-semibold text-gray-900">{currentItem.productDescription}</p>
                <p className="text-sm text-gray-600 mt-0.5">Batch: {currentItem.batchNo}</p>
                <div className="grid grid-cols-3 gap-4 mt-3">
                  <div className="text-center bg-white rounded-lg p-2 border border-gray-100">
                    <p className="text-xs text-gray-500">Available Good</p>
                    <p className="font-semibold text-gray-900">{currentItem.goodQty}</p>
                  </div>
                  <div className="text-center bg-white rounded-lg p-2 border border-gray-100">
                    <p className="text-xs text-gray-500">Available CDAM</p>
                    <p className="font-semibold text-gray-900">{currentItem.cdamQty}</p>
                  </div>
                  <div className="text-center bg-white rounded-lg p-2 border border-gray-100">
                    <p className="text-xs text-gray-500">Available DAM</p>
                    <p className="font-semibold text-gray-900">{currentItem.damQty}</p>
                  </div>
                </div>
                {!isSharedPallet && (
                  <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between items-center text-sm">
                    <span className="text-gray-600">Storage Locations Total:</span>
                    <span className="font-semibold text-green-600">
                      {storageData.reduce((sum, s) => sum + (s.palletCount || 0), 0)} pallets
                    </span>
                  </div>
                )}
              </div>

              {/* Shared Pallet Toggle */}
              <button
                type="button"
                onClick={() => setIsSharedPallet(!isSharedPallet)}
                className={`w-full flex items-start gap-3 p-3.5 rounded-lg border-2 text-left transition-colors ${
                  isSharedPallet
                    ? 'bg-amber-50 border-amber-400'
                    : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                  isSharedPallet ? 'bg-amber-500 border-amber-500' : 'border-gray-400'
                }`}>
                  {isSharedPallet && <i className="fas fa-check text-white text-xs"></i>}
                </div>
                <div>
                  <p className={`text-sm font-semibold ${isSharedPallet ? 'text-amber-800' : 'text-gray-700'}`}>
                    Shared Pallet — Dispatch product only (no pallet removal)
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Use this when the product shares a pallet with other items. Only quantities are tracked — the pallet stays in the warehouse.
                  </p>
                </div>
              </button>

              {/* Quantity Inputs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Good Qty Out</label>
                  {isSharedPallet ? (
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={itemForm.goodQtyOut === 0 ? '' : itemForm.goodQtyOut}
                      onChange={(e) => {
                        const digits = INPUT_SANITIZERS.digitsOnly(e.target.value);
                        setItemForm({ ...itemForm, goodQtyOut: digits === '' ? 0 : parseInt(digits, 10) });
                      }}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 transition-all duration-200"
                    />
                  ) : (
                    <div
                      className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-700"
                      title="Computed as the sum of Dispatch Qty across selected locations below"
                    >
                      {itemForm.goodQtyOut}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">CDAM Qty Out</label>
                  <div
                    className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
                    title="Retired — a location's stock can't be split into CDAM vs DAM, so LBD dispatch quantity is tracked entirely under DAM Qty Out"
                  >
                    {itemForm.cdamQtyOut}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">DAM Qty Out</label>
                  {isSharedPallet ? (
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={itemForm.damQtyOut === 0 ? '' : itemForm.damQtyOut}
                      onChange={(e) => {
                        const digits = INPUT_SANITIZERS.digitsOnly(e.target.value);
                        setItemForm({ ...itemForm, damQtyOut: digits === '' ? 0 : parseInt(digits, 10) });
                      }}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-500 transition-all duration-200"
                    />
                  ) : (
                    <div
                      className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-700"
                      title="Computed as the sum of Dispatch Qty across LBD locations below"
                    >
                      {itemForm.damQtyOut}
                    </div>
                  )}
                </div>
                {!isSharedPallet && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Pallets Out</label>
                    <div
                      className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-700"
                      title="Computed as the sum of Remove across selected locations below"
                    >
                      {itemForm.palletsOut}
                    </div>
                  </div>
                )}
              </div>

              {/* LBD qty cross-check — CDAM/DAM Qty Out stay manual because the backend
                  only tracks a single combined quantity per LBD location (no per-location
                  CDAM vs DAM split exists to auto-sum from), but this at least reflects
                  what was selected below so a mismatch is caught before saving. */}
              {!isSharedPallet && storageData.some((s) => s.isLbd) && (() => {
                const totalLbdDispatchQty = storageData
                  .filter((s) => s.isLbd)
                  .reduce((sum, s) => sum + (s.dispatchQty || 0), 0);
                const enteredTotal = itemForm.cdamQtyOut + itemForm.damQtyOut;
                return (
                  <div className="flex justify-between items-center text-sm p-2 bg-yellow-50 rounded">
                    <span className="text-gray-700">LBD Qty selected below (CDAM + DAM must match):</span>
                    <span className={`font-semibold ${
                      enteredTotal === totalLbdDispatchQty
                        ? 'text-green-600'
                        : enteredTotal > totalLbdDispatchQty
                        ? 'text-red-600'
                        : 'text-orange-600'
                    }`}>
                      {enteredTotal} / {totalLbdDispatchQty}
                      {enteredTotal === totalLbdDispatchQty && ' ✓'}
                      {enteredTotal > totalLbdDispatchQty && ' (Too many)'}
                      {enteredTotal < totalLbdDispatchQty && ' (Need more)'}
                    </span>
                  </div>
                );
              })()}

              {/* Storage Section */}
              {isSharedPallet ? (
                <div className="space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="fas fa-layer-group text-amber-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-amber-800">Shared Pallet Mode — good stock dispatched qty-only</p>
                      <p className="text-sm text-amber-700 mt-0.5">
                        No good pallets are removed; the shared pallet stays in the warehouse.
                        You can still remove dedicated LBD (damaged-goods) pallets below.
                      </p>
                    </div>
                  </div>

                  {storageData.some((s) => s.isLbd) && (
                    <div>
                      <div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:items-center mb-2">
                        <h4 className="font-semibold flex items-center gap-2 text-sm sm:text-base">
                          LBD / Damaged Pallets
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">LBD</span>
                        </h4>
                        <span className="text-xs text-gray-500">Select LBD pallets to remove (optional)</span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Zone</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Rack</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Location</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Available</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Remove</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {storageData.map((storage, index) =>
                              storage.isLbd ? (
                                <tr key={index}>
                                  <td className="px-4 py-2 text-sm">
                                    <span className="inline-flex items-center gap-1.5">
                                      {storage.zoneCode || '-'}
                                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">LBD</span>
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 text-sm">{storage.rackCode || '-'}</td>
                                  <td className="px-4 py-2 text-sm">{storage.locationCode || storage.zoneLocationCode || '-'}</td>
                                  <td className="px-4 py-2 text-sm text-right">{storage.palletCount}</td>
                                  <td className="px-4 py-2 text-right">
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      pattern="[0-9]*"
                                      value={storage.palletsToRemove === 0 ? '' : storage.palletsToRemove}
                                      onChange={(e) => {
                                        const digits = INPUT_SANITIZERS.digitsOnly(e.target.value);
                                        const newData = [...storageData];
                                        newData[index].palletsToRemove = digits === '' ? 0 : parseInt(digits, 10);
                                        setStorageData(newData);
                                      }}
                                      className="w-20 px-2 py-1 border rounded text-sm"
                                    />
                                  </td>
                                </tr>
                              ) : null,
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ) : storageLoading ? (
                <div className="flex justify-center py-4">
                  <Spinner />
                </div>
              ) : storageData.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">No storage locations found for this item</p>
                </div>
              ) : (
                <div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center mb-2">
                    <h4 className="font-semibold text-sm sm:text-base">Select Storage Locations</h4>
                    <div className="text-xs sm:text-sm bg-brand-50 px-3 py-1 rounded-lg w-fit">
                      <span className="text-brand-600 font-medium">
                        Total Available: {storageData.reduce((sum, s) => sum + (s.palletCount || 0), 0)} pallets
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:items-center text-sm mb-3 p-2 bg-yellow-50 rounded">
                    <span className="text-gray-700">Pallets to remove:</span>
                    <span className={`font-semibold ${
                      storageData.reduce((sum, s) => sum + (s.palletsToRemove || 0), 0) === itemForm.palletsOut
                        ? 'text-green-600'
                        : storageData.reduce((sum, s) => sum + (s.palletsToRemove || 0), 0) > itemForm.palletsOut
                        ? 'text-red-600'
                        : 'text-orange-600'
                    }`}>
                      {storageData.reduce((sum, s) => sum + (s.palletsToRemove || 0), 0)} / {itemForm.palletsOut}
                      {storageData.reduce((sum, s) => sum + (s.palletsToRemove || 0), 0) === itemForm.palletsOut && ' ✓'}
                      {storageData.reduce((sum, s) => sum + (s.palletsToRemove || 0), 0) > itemForm.palletsOut && ' (Too many)'}
                      {storageData.reduce((sum, s) => sum + (s.palletsToRemove || 0), 0) < itemForm.palletsOut && storageData.reduce((sum, s) => sum + (s.palletsToRemove || 0), 0) > 0 && ' (Need more)'}
                    </span>
                  </div>
                  {(() => {
                    // Shared row renderer for both tables below — a location's
                    // Available Qty / Dispatch Qty / Remove behavior is identical
                    // whether it's good stock or LBD; only which table it lands in differs.
                    const renderRow = (storage: any, index: number) => {
                      // Prefer the real per-location quantity (storedQty, set at GRN
                      // placement). Fall back to an estimate only for older rows that
                      // predate that field — relevant qty / total pallets of the same
                      // type (LBD pallets carry damaged stock, regular pallets carry good).
                      let availableQty = storage.storedQty;
                      if (availableQty == null) {
                        const relevantQty = storage.isLbd
                          ? (storage.cdamQty || 0) + (storage.damQty || 0)
                          : (storage.goodQty || 0);
                        const totalTypePallets = storageData
                          .filter((s) => !!s.isLbd === !!storage.isLbd)
                          .reduce((sum, s) => sum + (s.palletCount || 0), 0);
                        availableQty = totalTypePallets > 0 ? Math.round(relevantQty / totalTypePallets) : 0;
                      }
                      return (
                        <tr key={index}>
                          <td className="px-4 py-2 text-sm">
                            <span className="inline-flex items-center gap-1.5">
                              {storage.zoneCode || '-'}
                              {storage.isLbd && (
                                <span
                                  className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700"
                                  title="LBD (damaged-goods) pallet"
                                >
                                  LBD
                                </span>
                              )}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm">{storage.rackCode || '-'}</td>
                          <td className="px-4 py-2 text-sm">{storage.locationCode || storage.zoneLocationCode || '-'}</td>
                          <td className="px-4 py-2 text-sm text-right">{storage.palletCount}</td>
                          <td className="px-4 py-2 text-sm text-right text-gray-600">{availableQty}</td>
                          <td className="px-4 py-2 text-right">
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              value={storage.dispatchQty === 0 ? '' : storage.dispatchQty}
                              onChange={(e) => {
                                const newData = [...storageData];
                                const digits = INPUT_SANITIZERS.digitsOnly(e.target.value);
                                const raw = digits === '' ? 0 : parseInt(digits, 10);
                                // Dispatch Qty can never exceed what's actually available
                                // at this location — applies to LBD pallets too, so a
                                // partial quantity can be pulled from a single damaged
                                // pallet instead of only removing the whole thing.
                                const val = Math.min(raw, availableQty ?? 0);
                                // Remove is only ever auto-populated here, never typed
                                // directly — fully depleting this location's available
                                // qty means the pallet is leaving too; anything less
                                // means it stays, so Remove resets to 0.
                                const palletsToRemove =
                                  val > 0 && val === availableQty ? newData[index].palletCount || 1 : 0;
                                newData[index] = { ...newData[index], dispatchQty: val, palletsToRemove };
                                setStorageData(newData);
                              }}
                              className="w-20 px-2 py-1 border rounded text-sm"
                            />
                          </td>
                          <td className="px-4 py-2 text-right">
                            <div
                              className="w-20 px-2 py-1 text-sm text-gray-700 ml-auto"
                              title="Auto-set when Dispatch Qty fully depletes this location"
                            >
                              {storage.palletsToRemove || 0}
                            </div>
                          </td>
                        </tr>
                      );
                    };

                    // Bifurcate by isLbd while preserving each row's original index into
                    // storageData — renderRow's onChange writes back by that index, so it
                    // must stay tied to storageData's real position, not the filtered list's.
                    const indexed = storageData.map((s, i) => ({ s, i }));
                    const normalRows = indexed.filter(({ s }) => !s.isLbd);
                    const lbdRows = indexed.filter(({ s }) => s.isLbd);

                    const columns = (
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Zone</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Rack</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Location</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Available</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Available Qty</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Dispatch Qty</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Remove</th>
                      </tr>
                    );

                    return (
                      <div className="space-y-4">
                        {normalRows.length > 0 && (
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">{columns}</thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {normalRows.map(({ s, i }) => renderRow(s, i))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {lbdRows.length > 0 && (
                          <div>
                            <h5 className="text-xs font-semibold uppercase tracking-wide text-red-700 mb-1.5">
                              LBD / Damaged Pallets
                            </h5>
                            <div className="overflow-x-auto rounded-lg border border-red-100">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-red-50">{columns}</thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {lbdRows.map(({ s, i }) => renderRow(s, i))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
                <button
                  onClick={() => {
                    setShowStorageModal(false);
                    setEditingItemId(null);
                    setEditingItemStorageSelections(null);
                  }}
                  className="w-full sm:w-auto min-h-[44px] px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStorageSelection}
                  disabled={addItemMutation.isPending || updateItemMutation.isPending}
                  className="w-full sm:w-auto min-h-[44px] px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {(addItemMutation.isPending || updateItemMutation.isPending) ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {editingItemId ? 'Updating...' : 'Adding...'}
                    </>
                  ) : (
                    editingItemId ? 'Update Item' : 'Add Item'
                  )}
                </button>
              </div>
            </div>
          )}
        </Modal>
      </div>
      <ConfirmDialog />
    </Layout>
  );
}